import React, { useEffect, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { useUserContext } from '../contexts'
import useUpdateUserSettings from '../hooks/useUpdateUserSettings'
import useFetchUser from '../hooks/useFetchUser'
import { isAdmin } from '../utils'
import * as ga from '../lib/ga'
import { Link } from 'react-router-dom'
import { PageHeader } from '../components/page-header'
import { PageBody } from '../components/page-body'
import EmailStatusEmail from '../components/shared/email/emailStatusEmail'
import useOverlayNotification from '../hooks/useOverlayNotification'
import Checkbox from '../components/shared/checkbox'

const UPDATE_PASSWORD = gql`
  mutation UpdatePassword($oldPassword: String!, $newPassword: String!) {
    appUpdatePassword(oldPassword: $oldPassword, newPassword: $newPassword)
  }
`

function SettingsPage() {
  const { user } = useUserContext()
  const { t } = useTranslation('userSettings')
  const { refetchUser } = useFetchUser()
  const { showSuccessNotification } = useOverlayNotification()
  const { updateUserSettings } = useUpdateUserSettings()
  const [errorPass, setErrorPass] = useState(null)
  let formOldPassword
  let formNewPassword

  const userCompany = user?.company
  const userIsAdmin = isAdmin(user?.role)
  const userName = user?.name
  const userEmail = user?.email
  const userAcceptedDate = user?.acceptedDate

  useEffect(() => {
    refetchUser()
  }, [refetchUser])

  const [appUpdatePassword] = useMutation(UPDATE_PASSWORD, {
    onCompleted: () => {
      showSuccessNotification()
    },
    onError(err) {
      const error = `${err}`.split(':').reverse()[0]
      setErrorPass(error)
    }
  })

  const handleChangePassword = (e) => {
    e.preventDefault()

    // TODO: Add check length of new and old password
    // TODO: Add check for strength of NewPassword

    const variables = {
      oldPassword: formOldPassword.value,
      newPassword: formNewPassword.value
    }

    appUpdatePassword({
      variables
    })
    formOldPassword.value = ''
    formNewPassword.value = ''

    setErrorPass(null)

    ga.event({
      action: 'update_password'
    })
  }

  const handleChangeSettings = (fieldName, value) => {
    updateUserSettings({
      [fieldName]: !value
    })
  }

  return (
    <>
      <PageHeader pageName={t('header.title')} />

      <PageBody>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('cardMyInformation.title')}
            </h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
            <dl className="sm:divide-y sm:divide-gray-200">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  {t('content:company')}
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {userCompany}
                  {userIsAdmin && 'Admin'}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">{t('content:name')}</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {userName}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  {t('content:email')}
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {userEmail} <EmailStatusEmail email={userEmail} />
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  {t('cardMyInformation.conditionsAcceptedOn')}
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {userAcceptedDate && new Date(userAcceptedDate).toLocaleString('sv')}
                  <br />
                  <Link to="/legal">
                    <a>{t('cardMyInformation.readConditions')}</a>
                  </Link>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="bg-white mt-4 shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('notifications.title')}
            </h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
            <dl className="sm:divide-y sm:divide-gray-200">
              <div className="py-4 sm:py-5 sm:px-6">
                <Checkbox
                  id="noNotificationsSystemguide"
                  onChange={handleChangeSettings}
                  text={t('notifications.notifyAboutSystemGuide')}
                  checked={!user?.noNotificationsSystemguide}
                  rawChangeEvent={false}
                />
              </div>
              <div className="py-4 sm:py-5 sm:px-6">
                <Checkbox
                  id="noNotificationsInquiry"
                  onChange={handleChangeSettings}
                  text={t('notifications.notifyAbouInquiry')}
                  checked={!user?.noNotificationsInquiry}
                  rawChangeEvent={false}
                />
              </div>
            </dl>
          </div>
        </div>

        <div className="hidden sm:block" aria-hidden="true">
          <div className="py-5">
            <div className="border-t border-gray-200" />
          </div>
        </div>

        <div className="mt-10 sm:mt-0">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <div className="px-4 sm:px-0">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  <a>{t('changePassword.title')}</a>
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t('changePassword.subtitle')}
                </p>
              </div>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <form onSubmit={handleChangePassword}>
                <div className="shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-3">
                        <label
                          htmlFor="formOldPassword"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('cardChangePassword.currentPassword')}
                        </label>
                        <input
                          type="password"
                          name="formOldPassword"
                          id="formOldPassword"
                          ref={(node) => {
                            formOldPassword = node
                          }}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-3">
                        <label
                          htmlFor="formNewPassword"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('cardChangePassword.newPassword')}
                        </label>
                        <input
                          type="password"
                          name="formNewPassword"
                          id="formNewPassword"
                          ref={(node) => {
                            formNewPassword = node
                          }}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                    <button
                      type="submit"
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {t('cardChangePassword.btnSaveSettings')}
                    </button>
                  </div>
                </div>
                {errorPass && (
                  <div style={{ textAlign: 'center', color: 'red', padding: '10px' }}>
                    {errorPass}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </PageBody>
    </>
  )
}

export default SettingsPage
